import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { IApplicationFormStep } from '../types';

interface IApplicationFormContext {
  data: any;
  currentStep: number;
  completedSteps: number[];
  formJSON: IApplicationFormStep[];
  setFormJSON: React.Dispatch<React.SetStateAction<IApplicationFormStep[]>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  updateData: (values: any) => void;
}

const defaultContextValue: IApplicationFormContext = {
  data: {},
  currentStep: 0,
  completedSteps: [],
  formJSON: [],
  updateData: () => {
    throw new Error('Not Implemented');
  },
  setCurrentStep: () => {
    throw new Error('Not Implemented');
  },
  setFormJSON: () => {
    throw new Error('Not Implemented');
  },
};

const ApplicationFormContext = createContext<IApplicationFormContext>(defaultContextValue);

const useApplicationFormProvider = (initialValues?: any): IApplicationFormContext => {
  const [data, setData] = useState<any>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [formJSON, setFormJSON] = useState<IApplicationFormStep[]>([]);
  const updateData = useCallback(
    (values: any) => {
      setData((prev: any) => ({ ...prev, ...values }));
      setCompletedSteps((prev) => [...prev, currentStep]);
    },
    [currentStep]
  );

  useEffect(() => {
    if (!data) {
      updateData(initialValues);
    }
  }, [initialValues, updateData, data]);

  useEffect(
    () => () => {
      setData({});
      setCurrentStep(0);
      setCompletedSteps([]);
    },
    []
  );

  return {
    data,
    currentStep,
    completedSteps,
    formJSON,
    updateData,
    setCurrentStep,
    setFormJSON,
  };
};

interface ApplicationFormProviderProps {
  children?: React.ReactNode;
  initialValues?: any;
}

export const ApplicationFormProvider = (props: ApplicationFormProviderProps) => {
  const { children, initialValues } = props;
  const value = useApplicationFormProvider(initialValues);
  return <ApplicationFormContext.Provider value={value}>{children}</ApplicationFormContext.Provider>;
};

export const useApplicationFormContext = () => useContext(ApplicationFormContext);
